import { Tag } from '@components/UI-Components/Tag'
import { DropTarget } from '@components/Utils/DropTarget'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import { useObserver } from 'mobx-react-lite'
import React, { useRef, useState } from 'react'
import * as css from './BulkOperationTabContent.css'
import { CustomPreviewDraggableSource } from '@components/Utils/CustomPreviewDraggableSource'
import classNames from 'classnames'
import SelectionByMouseDrag from '@components/Utils/SelectionByMouseDrag'
import { DraggableSource } from '@components/Utils/DraggableSource'
import { Tooltip } from '@material-ui/core'
import { SelectAllNewIcon } from '@components/UI-Components/svg'
import { useTranslation } from 'react-i18next'

interface ILeftSideBulkOperationTabContent {
  selectedChipsForDrag: any[]
  unSelectedEntities: any[]
  draggableSectionTitle: string
  bulkOpSelectedTab: string
  handleChipsDeselection: Function
  handleChipsSelectionByMouseDrag: any
  setIsWindowSelectionGoingOn: any
  mouseDownHandlerForChips: any
  removeBulkOpSelectedEntity: any
  onMouseUpHandlerOnChip: any
  onSelectAllTags: any
}

export const LeftSideBulkOperationTabContent: React.FunctionComponent<
  ILeftSideBulkOperationTabContent
> = ({
  selectedChipsForDrag,
  unSelectedEntities,
  draggableSectionTitle,
  bulkOpSelectedTab,
  handleChipsDeselection,
  handleChipsSelectionByMouseDrag,
  setIsWindowSelectionGoingOn,
  mouseDownHandlerForChips,
  removeBulkOpSelectedEntity,
  onMouseUpHandlerOnChip,
  onSelectAllTags
}) => {
  return useObserver(function useHook() {
    const itemsRef = useRef([])
    const containerRef = useRef(null) // Reference to the container element
    const [isMultipleDragActive, setIsMultipleDragActive] = useState(false)
    const { t } = useTranslation()
    const [windowSelectionHeaderPosition, setWindowSelectionHeaderPosition] = useState(0)
    const HEADER_OFFSET_HEIGHT = 35
    const customDraggableChildren = (
      <div className={css.customDraggableEntitySection}>
        {selectedChipsForDrag.map((entity, index) => {
          const clusterEntity = 'RGBHex' in entity
          return (
            <Chip
              key={`entity_${index}`}
              className={classNames(css.entityChip, {
                [css.clusterChip]: clusterEntity,
                [css.selectedChip]: true
              })}
              classes={{
                label: clusterEntity ? css.clusterChipLabel : css.entityChipLabel
              }}
              label={
                <Tag
                  className={css.circle}
                  key={`entityLabel_${index}`}
                  color={clusterEntity ? entity.RGBHex : null}
                  name={entity.Description || entity.Name}
                  tooltipPlacement={'bottom-start'}
                />
              }
            />
          )
        })}
      </div>
    )
    const handleMouseDownEvent = entity => {
      if (
        selectedChipsForDrag?.length &&
        selectedChipsForDrag?.some(ele => ele?.Id === entity?.Id)
      ) {
        setIsMultipleDragActive(true)
      } else {
        setIsMultipleDragActive(false)
      }
    }
    return (
      <DropTarget
        className={css.unselectedEntityContainer}
        canDrop
        handleDrop={({
          type,
          selectedChipsForDrag,
          isItemDrag,
          item,
          isRightHandSideDrag
        }) => {
          if (isRightHandSideDrag) {
            const items = isItemDrag ? [item] : selectedChipsForDrag
            removeBulkOpSelectedEntity(type, items, bulkOpSelectedTab)
          }
        }}
      >
        <div onClick={() => handleChipsDeselection()}>
          <SelectionByMouseDrag
            items={unSelectedEntities}
            itemsRef={itemsRef}
            handleSelectionByMouseDrag={handleChipsSelectionByMouseDrag}
            canDragAndDrop={false}
            className={css.windowSelectContainer}
            containerRef={containerRef}
            setIsWindowSelectionGoingOn={setIsWindowSelectionGoingOn}
            setWindowSelectionHeaderPosition={setWindowSelectionHeaderPosition}
            windowSelectionHeaderPosition={windowSelectionHeaderPosition}
            headerOffsetHeight={HEADER_OFFSET_HEIGHT}
          >
            <div
              className={css.headerWrapper}
              onMouseDown={e => {
                e.preventDefault()
                const containerPosition = containerRef?.current?.getBoundingClientRect()
                const clickPosition = Math.round(e?.clientY - containerPosition?.y)
                setWindowSelectionHeaderPosition(clickPosition)
              }}
            >
              <Typography className={css.dragSectionHeader}>
                {draggableSectionTitle}
              </Typography>
              <Tooltip title={t('common.label.selectAll', 'Select All')}>
                <div className={css.iconWrapper}>
                  <img
                    src={SelectAllNewIcon}
                    alt='Select all'
                    className={classNames(css.icon, {
                      [css.disabledIcon]:
                        unSelectedEntities?.length === selectedChipsForDrag?.length ||
                        !unSelectedEntities?.length
                    })}
                    onClick={e => {
                      e.stopPropagation()
                      if (
                        unSelectedEntities?.length ||
                        unSelectedEntities?.length !== selectedChipsForDrag?.length
                      ) {
                        onSelectAllTags(unSelectedEntities, false)
                      }
                    }}
                  />
                </div>
              </Tooltip>
            </div>
            <CustomPreviewDraggableSource
              key={bulkOpSelectedTab}
              className={css.draggbleSource}
              canDrag={isMultipleDragActive}
              libraryItem={{
                id: bulkOpSelectedTab,
                type: bulkOpSelectedTab,
                selectedChipsForDrag,
                isRightHandSideDrag: false
              }}
              customDraggableChildren={customDraggableChildren}
              containerRef={containerRef}
            >
              <div className={css.draggableEntitySection}>
                {unSelectedEntities?.map((entity, index) => {
                  const selectedEntity = selectedChipsForDrag?.find(
                    ele => ele?.Id === entity?.Id
                  )
                  const clusterEntity = 'RGBHex' in entity
                  return (
                    <DraggableSource
                      key={entity?.Id}
                      canDrag={!isMultipleDragActive}
                      libraryItem={{
                        id: entity?.Id,
                        type: bulkOpSelectedTab,
                        isItemDrag: true,
                        item: entity,
                        isRightHandSideDrag: false
                      }}
                    >
                      <div
                        className={css.chipWrapper}
                        ref={el => (itemsRef.current[index] = el)}
                        onMouseDown={e => {
                          handleMouseDownEvent(entity)
                          mouseDownHandlerForChips(e, entity, unSelectedEntities)
                        }}
                        onMouseUp={e => {
                          onMouseUpHandlerOnChip(e, entity)
                        }}
                      >
                        <Chip
                          key={`entity_${index}`}
                          className={classNames(css.entityChip, {
                            [css.clusterChip]: clusterEntity,
                            [css.selectedChip]: !!selectedEntity?.Id
                          })}
                          classes={{
                            label: clusterEntity
                              ? css.clusterChipLabel
                              : css.entityChipLabel
                          }}
                          label={
                            <Tag
                              className={css.circle}
                              key={`entityLabel_${index}`}
                              color={clusterEntity ? entity.RGBHex : null}
                              name={entity.Description || entity.Name}
                              tooltipPlacement={'bottom-start'}
                            />
                          }
                        />
                      </div>
                    </DraggableSource>
                  )
                })}
              </div>
            </CustomPreviewDraggableSource>
          </SelectionByMouseDrag>
        </div>
      </DropTarget>
    )
  })
}
